import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import useDarkMode from '../../../../hooks/useDarkMode';
import './PhoneNumberInput.css';

const PhoneNumberInput = ({
	value,
	onChange,
	onBlur,
	defaultCountryIso,
	disabled = false,
	inputProps = {},
	className = '',
	style = {},
	required = false,
	error = false,
}) => {
	const { darkModeStatus } = useDarkMode();
	const [internalValue, setInternalValue] = useState(value || '');
	const [touched, setTouched] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	// Sync with external value - IMPORTANT: update whenever value prop changes
	useEffect(() => {
		// Only update if not focused and value is different
		if (!isFocused && value !== internalValue) {
			setInternalValue(value || '');
		}
	}, [value, isFocused]);

	const country = useMemo(
		() => (defaultCountryIso ? defaultCountryIso.toLowerCase() : undefined),
		[defaultCountryIso],
	);

	const handleChange = useCallback(
		(val, ctry, e, formattedValue) => {
			setInternalValue(val);
			if (onChange) {
				onChange(val, ctry, e, formattedValue);
			}
		},
		[onChange],
	);

	const handleFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const handleBlur = useCallback(
		(e) => {
			setTouched(true);
			setIsFocused(false);
			if (onBlur) {
				onBlur(e);
			}
		},
		[onBlur],
	);

	const isCodeSelected = useMemo(() => {
		const hasAnyValue = typeof internalValue === 'string' && internalValue.trim().length > 0;
		return Boolean(country) || hasAnyValue;
	}, [country, internalValue]);

	const containerClass = `sd-phone-input${isCodeSelected ? '' : ' sd-phone-empty'} ${
		className || ''
	}`.trim();

	// Determine border color based on validation state
	const borderStyle = error
		? { border: '2px solid red' }
		: touched && required && !internalValue
		? { border: '2px solid red' }
		: {};

	// Apply same border to the flag/dropdown button when there's an error
	const buttonBorderStyle = borderStyle.border
		? borderStyle.border
		: darkModeStatus
		? '1px solid #353b3fff'
		: '1px solid #ecececc9';

	return (
		<div style={{ width: '100%' }}>
			<PhoneInput
				country={country}
				value={internalValue}
				onChange={handleChange}
				disabled={disabled}
				containerClass={containerClass}
				buttonClass='sd-phone-flag'
				dropdownClass='sd-phone-dropdown'
				searchClass='sd-phone-search sd-phone-search-box'
				inputClass={'form-control'}
				inputProps={{
					name: 'phone',
					required,
					autoFocus: false,
					onFocus: handleFocus,
					...inputProps,
				}}
				enableSearch
				countryCodeEditable={false}
				placeholder={''}
				containerStyle={{ width: '100%', ...(style || {}) }}
				inputStyle={{
					width: '100%',
					background: darkModeStatus ? '#212529' : '#f8f9fa',
					color: darkModeStatus ? '#fff' : '#323232',
					borderRadius: '1rem',
					border: darkModeStatus ? '1px solid #353b3fff' : '1px solid #ecececc9',
					boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
					...borderStyle,
				}}
				buttonStyle={{
					borderTopLeftRadius: '1rem',
					borderBottomLeftRadius: '1rem',
					background: darkModeStatus ? '#212529' : '#f8f9fa',
					border: buttonBorderStyle,
				}}
				dropdownStyle={{
					background: darkModeStatus ? '#212529' : '#fff',
					color: darkModeStatus ? '#fff' : '#323232',
					borderRadius: '1rem',
					border: darkModeStatus ? '1px solid #5c5d5f' : '1px solid #ecececc9',
				}}
				onBlur={handleBlur}
			/>
		</div>
	);
};

export default PhoneNumberInput;
